import { useCallback, useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/Badge";
import { I } from "@/components/Icon";
import type { EntityItem } from "@/components/MultiEntityPicker";
import { SingleEntityPicker } from "@/components/SingleEntityPicker";
import { useApps, useCampaignsList, useCustomProductPages, useEventTypes } from "@/lib/api-hooks";
import { initialsFor, tintFor } from "@/lib/overview-helpers";
import { CampaignMultiPicker } from "@/pages/campaign-studio/components/CampaignMultiPicker";

import {
  useCppAvailableAdGroups,
  useCreateCppTestBatch,
  useEstimateCppDuration,
} from "./hooks";
import { CPP_METRIC_META } from "./types";

/** The server's own words, with the busy-ad-groups hint kept as a hint.
 *
 * Naming one cause for every failure is worse than naming none: on
 * 2026-08-04 a 500 (`resp.items` on a model whose field is `rows`) was
 * displayed as "check the ad groups aren't already in a test", and the ad
 * groups were fine — the account had no tests at all. */
function createErrorText(error: unknown): string {
  const msg = (error as { response?: { data?: { error?: { message?: string } } } })
    ?.response?.data?.error?.message;
  return msg
    ? `Couldn’t create the test: ${msg}`
    : "Couldn’t create the test. If the ad groups are already in a running test, stop it first.";
}
import type { CppBatchCreateTestRequest, CppMetric, CppVariantIn } from "./types";

const WIZ_STEPS = [
  { n: 1, label: "Where" },
  { n: 2, label: "Pages" },
  { n: 3, label: "Split" },
  { n: 4, label: "Metric" },
  { n: 5, label: "Confidence" },
  { n: 6, label: "Finish" },
  { n: 7, label: "Launch" },
];

const METRIC_OPTIONS: { id: CppMetric; label: string; hint: string }[] = [
  { id: "ipm", label: "Installs per mille (IPM)", hint: "Installs ÷ impressions. Resolves fast." },
  { id: "cpi", label: "Cost per install (CPI)", hint: "Spend ÷ installs. Resolves fast." },
  { id: "cpa", label: "Cost per in-app action (CPA)", hint: "Pick the event + day after install." },
  { id: "roas", label: "Return on ad spend (ROAS)", hint: "Revenue ÷ spend at a chosen day." },
];

const DAY_OPTIONS = ["1", "3", "7", "14", "30"];

function WizField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="wiz-field">
      <label className="wiz-label">{label}</label>
      {children}
      {hint && <div className="wiz-hint">{hint}</div>}
    </div>
  );
}

// One campaign's ad-group sub-tree in the multi-campaign step. Fetches its own
// ad groups (reusing the same hook + cache) and reports the non-busy ids up so
// the parent can drive the global Select-all + counts.
function CampaignGroupTree({
  campaignId,
  campaignName,
  selected,
  onToggle,
  onSelectAll,
  onGroupsLoaded,
}: {
  campaignId: number;
  campaignName: string;
  selected: number[];
  onToggle: (agid: number) => void;
  onSelectAll: (ids: number[]) => void;
  onGroupsLoaded: (cid: number, freeIds: number[]) => void;
}) {
  const ag = useCppAvailableAdGroups(campaignId);
  const groups = useMemo(() => ag.data || [], [ag.data]);
  const freeIds = useMemo(
    () => groups.filter((g) => !g.busy).map((g) => g.ad_group_id),
    [groups],
  );
  const freeKey = freeIds.join(",");
  useEffect(() => {
    onGroupsLoaded(campaignId, freeIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, freeKey]);

  const allOn = freeIds.length > 0 && freeIds.every((id) => selected.includes(id));
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <b style={{ fontSize: 14 }}>{campaignName}</b>
        <span className="muted" style={{ fontSize: 12 }}>
          {selected.length}/{freeIds.length} groups
        </span>
        {freeIds.length > 0 && (
          <button
            className="btn ghost sm"
            style={{ marginLeft: "auto" }}
            onClick={() => onSelectAll(allOn ? [] : freeIds)}
          >
            {allOn ? "Clear" : "Select all"}
          </button>
        )}
      </div>
      <div className="wiz-checks">
        {ag.isLoading ? (
          <span className="muted" style={{ fontSize: 13 }}>Loading ad groups…</span>
        ) : groups.length === 0 ? (
          <span className="muted" style={{ fontSize: 13 }}>No ad groups in this campaign.</span>
        ) : (
          groups.map((g) => {
            const on = selected.includes(g.ad_group_id);
            return (
              <button
                key={g.ad_group_id}
                className={"wiz-check " + (on ? "active" : "")}
                disabled={g.busy}
                title={g.busy ? "Already has an active test" : ""}
                onClick={() => onToggle(g.ad_group_id)}
              >
                <span className={"mx-check " + (on ? "on" : "")}>{on && I.check}</span>
                <span className="wiz-check-meta">
                  <b>{g.name || `Ad group #${g.ad_group_id}`}</b>
                  <span className="muted">
                    {g.busy ? "Busy — already in a test" : g.status || ""}
                  </span>
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

type CppRow = {
  creative_id: number;
  name: string;
  creative_type: string;
  product_page_id: string | null;
  screenshots: string[];
};

function cppKey(r: { product_page_id: string | null }): string {
  return r.product_page_id ?? "default";
}

// Three small real screenshots (or neutral placeholders while they stream
// in) — replaces the black phone-thumb placeholder in the page list +
// split steps. Same markup/CSS as the Results-table mini preview.
function CppMiniShots({ shots }: { shots: string[] }) {
  const items = shots.slice(0, 3);
  return (
    <span className="cpp-minishots">
      {items.length > 0
        ? items.map((url, i) => (
            <span key={i} className="cpp-minishot">
              <img src={url} alt="" loading="lazy" />
            </span>
          ))
        : [0, 1, 2].map((i) => <span key={i} className="cpp-minishot ph" />)}
    </span>
  );
}

export function CppWizard({ onClose, onLaunch }: { onClose: () => void; onLaunch: () => void }) {
  const apps = useApps();
  const [step, setStep] = useState(1);
  const [appId, setAppId] = useState<number | null>(null);
  // Multi-campaign: which campaigns are selected, and per-campaign which ad
  // groups. `loadedGroups` mirrors each campaign's non-busy ad-group ids
  // (reported up by the sub-trees) so the global Select-all + counts work.
  const [campaignIds, setCampaignIds] = useState<Set<number>>(new Set());
  const [groupSel, setGroupSel] = useState<Record<number, number[]>>({});
  const [loadedGroups, setLoadedGroups] = useState<Record<number, number[]>>({});
  const [pickedKeys, setPickedKeys] = useState<string[]>([]);
  const [metric, setMetric] = useState<CppMetric>("ipm");
  const [event, setEvent] = useState("");
  const [day, setDay] = useState("7");
  const [confidence, setConfidence] = useState<90 | 95 | 99>(95);
  const [threshold, setThreshold] = useState(3);
  const [autoReplace, setAutoReplace] = useState(false);
  const [durOn, setDurOn] = useState(false);
  const [capOn, setCapOn] = useState(false);
  const [maxDur, setMaxDur] = useState(21);
  const [spendCap, setSpendCap] = useState(3000);
  const [noWin, setNoWin] = useState<"keep" | "manual">("keep");
  const [name, setName] = useState("");
  const [split, setSplit] = useState<number[] | null>(null);

  // default the app to the first one once apps load
  useEffect(() => {
    if (appId == null && apps.data && apps.data.length) setAppId(apps.data[0].id);
  }, [apps.data, appId]);

  const app = apps.data?.find((a) => a.id === appId) || null;
  const appItems = useMemo<EntityItem[]>(
    () =>
      (apps.data ?? []).map((a) => ({
        id: a.id,
        name: a.name,
        sub: a.bundle_id && !a.bundle_id.startsWith("asa-") ? a.bundle_id : "",
        tint: tintFor(a.id),
        initials: initialsFor(a.name),
        iconUrl: a.icon_url,
      })),
    [apps.data],
  );
  const asaAppId = app?.asa_app_id ?? null;
  const campaigns = useCampaignsList(appId);
  const cpps = useCustomProductPages(asaAppId);
  const events = useEventTypes();
  const createMut = useCreateCppTestBatch();
  const estimateMut = useEstimateCppDuration();

  const campaignName = (cid: number) =>
    (campaigns.data || []).find((c) => c.campaign_id === cid)?.name ||
    `Campaign #${cid}`;
  // Total / available group counts + the "first" (campaign, group) used for
  // the impression estimate step.
  const totalSelGroups = Object.values(groupSel).reduce((a, b) => a + b.length, 0);
  const totalAvailGroups = [...campaignIds].reduce(
    (a, cid) => a + (loadedGroups[cid]?.length || 0),
    0,
  );
  const firstCampaign = [...campaignIds].find((cid) => (groupSel[cid] || []).length > 0) ?? null;
  const firstGroup = firstCampaign != null ? groupSel[firstCampaign][0] : null;

  const onCampaignsChange = (next: Set<number>) => {
    setCampaignIds(next);
    // Drop group selections for campaigns no longer chosen.
    setGroupSel((g) => {
      const out: Record<number, number[]> = {};
      next.forEach((cid) => {
        if (g[cid]) out[cid] = g[cid];
      });
      return out;
    });
  };
  const onGroupsLoaded = useCallback((cid: number, ids: number[]) => {
    setLoadedGroups((m) => {
      const prev = m[cid];
      if (prev && prev.length === ids.length && prev.every((x, i) => x === ids[i])) return m;
      return { ...m, [cid]: ids };
    });
  }, []);
  const toggleGroup = (cid: number, agid: number) =>
    setGroupSel((g) => {
      const cur = g[cid] || [];
      return {
        ...g,
        [cid]: cur.includes(agid) ? cur.filter((x) => x !== agid) : [...cur, agid],
      };
    });
  const setCampaignGroups = (cid: number, ids: number[]) =>
    setGroupSel((g) => ({ ...g, [cid]: ids }));
  const selectAllGroups = () => {
    const out: Record<number, number[]> = {};
    campaignIds.forEach((cid) => {
      if (loadedGroups[cid]?.length) out[cid] = [...loadedGroups[cid]];
    });
    setGroupSel(out);
  };

  const cppRows = (cpps.data?.rows || []) as CppRow[];
  const picked = pickedKeys
    .map((k) => cppRows.find((r) => cppKey(r) === k))
    .filter(Boolean) as CppRow[];
  const variantCount = picked.length;

  // Header context — once a campaign (and optionally ad-groups) is picked,
  // show it on every step so the user always sees what this test is for.
  const agLabel = useMemo(() => {
    if (totalSelGroups === 0) return null;
    const nCamp = [...campaignIds].filter((cid) => (groupSel[cid] || []).length > 0).length;
    return `${nCamp} ${nCamp === 1 ? "campaign" : "campaigns"} · ${totalSelGroups} ${totalSelGroups === 1 ? "group" : "groups"}`;
  }, [campaignIds, groupSel, totalSelGroups]);

  const splitVals = useMemo(() => {
    if (split && split.length === variantCount) return split;
    if (variantCount === 0) return [];
    const base = Math.floor(100 / variantCount);
    const arr = Array(variantCount).fill(base);
    arr[0] += 100 - base * variantCount;
    return arr;
  }, [variantCount, split]);
  const splitSum = splitVals.reduce((a, b) => a + b, 0);
  const metricMeta = CPP_METRIC_META[metric];

  // estimate (impression-based metrics) on entering step 5 — representative
  // of the first selected (campaign, group).
  useEffect(() => {
    if (step === 5 && metricMeta.impressionBased && firstCampaign && firstGroup) {
      estimateMut.mutate({
        campaign_id: firstCampaign,
        ad_group_id: firstGroup,
        variant_count: Math.max(2, variantCount),
        metric,
        min_uplift_pct: threshold,
        confidence_level: confidence,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, metric, threshold, variantCount, confidence]);

  const canNext =
    step === 1
      ? campaignIds.size > 0 && totalSelGroups > 0
      : step === 2
        ? variantCount >= 2
        : step === 3
          ? splitSum === 100
          : step === 4
            ? metric !== "cpa" || !!event
            : step === 7
              ? name.trim().length > 0
              : true;

  const setSplitAt = (i: number, v: string) => {
    const arr = [...splitVals];
    arr[i] = Math.max(0, Math.min(100, parseInt(v || "0", 10)));
    setSplit(arr);
  };
  const toggleKey = (k: string) => {
    setPickedKeys((ks) => (ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k]));
    setSplit(null);
  };

  function buildPayload(): CppBatchCreateTestRequest {
    const variants: CppVariantIn[] = picked.map((r, i) => ({
      product_page_id: r.product_page_id,
      creative_id: r.creative_id || null,
      is_default_page: (r.creative_type || "").toUpperCase().includes("DEFAULT") || r.product_page_id == null,
      display_name: r.name,
      target_split_pct: splitVals[i],
    }));
    const campaignsArr = [...campaignIds]
      .map((cid) => ({ campaign_id: cid, ad_group_ids: groupSel[cid] || [] }))
      .filter((c) => c.ad_group_ids.length > 0);
    return {
      app_id: appId!,
      name: name.trim(),
      metric,
      metric_event: metric === "cpa" ? event : null,
      metric_day: metric === "cpa" || metric === "roas" ? parseInt(day, 10) : null,
      confidence_level: confidence,
      min_uplift_pct: threshold,
      auto_apply: autoReplace,
      stop_max_days: durOn ? maxDur : null,
      stop_spend_cap: capOn ? spendCap : null,
      on_no_winner: noWin,
      variants,
      campaigns: campaignsArr,
    };
  }

  const next = () => {
    if (step < 7) {
      setStep(step + 1);
      return;
    }
    createMut.mutate(buildPayload(), { onSuccess: () => onLaunch() });
  };
  const back = () => (step > 1 ? setStep(step - 1) : onClose());

  return (
    <div className="cpp-wiz">
      <div className="cpp-wiz-top">
        <div className="cpp-wiz-titles">
          <button className="btn ghost sm" onClick={onClose} style={{ marginLeft: -8 }}>
            <span style={{ transform: "rotate(180deg)", display: "inline-flex" }}>{I.chevron}</span> Cancel
          </button>
          <h1 className="page-title" style={{ fontSize: 28, marginTop: 6 }}>
            Create a <em>CPP test</em>
          </h1>
          {agLabel && (
            <div className="cpp-wiz-context" title={`${app?.name ?? ""} · ${agLabel}`}>
              {app?.name && (
                <>
                  <span className="cpp-wiz-ctx-app">{app.name}</span>
                  <span className="cpp-wiz-ctx-sep">{I.chevron}</span>
                </>
              )}
              <span className="cpp-wiz-ctx-camp">{agLabel}</span>
            </div>
          )}
        </div>
        <div className="cpp-stepper">
          {WIZ_STEPS.map((s, i) => (
            <div key={s.n} style={{ display: "contents" }}>
              <button
                className={"cpp-step " + (step === s.n ? "active" : step > s.n ? "done" : "")}
                onClick={() => step > s.n && setStep(s.n)}
              >
                <span className="cpp-step-dot">{step > s.n ? I.check : s.n}</span>
                <span className="cpp-step-lbl">{s.label}</span>
              </button>
              {i < WIZ_STEPS.length - 1 && (
                <span className={"cpp-step-line " + (step > s.n ? "done" : "")} />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="card cpp-wiz-card">
        <div className="cpp-wiz-body">
          {step === 1 && (
            <div className="wiz-pane">
              <div className="wiz-pane-head">
                <h3>Where are we testing?</h3>
                <p>
                  Pick the app and one or more campaigns, then choose ad groups under each.
                  Every selected group runs an independent test.
                </p>
              </div>
              <div className="wiz-grid-2">
                <WizField label="App">
                  <SingleEntityPicker<number>
                    items={appItems}
                    value={appId ?? -1}
                    width={300}
                    onChange={(v) => {
                      setAppId(v);
                      setCampaignIds(new Set());
                      setGroupSel({});
                      setLoadedGroups({});
                      setPickedKeys([]);
                    }}
                  />
                </WizField>
                <WizField label="Campaigns" hint="Pick any number — each shows its ad groups below.">
                  <CampaignMultiPicker
                    appId={appId}
                    selected={campaignIds}
                    onChange={onCampaignsChange}
                  />
                </WizField>
              </div>
              {campaignIds.size > 0 && (
                <WizField
                  label="Ad groups"
                  hint="Tick the groups to test in each campaign. Busy groups already have a test."
                >
                  <div style={{ display: "flex", gap: 10, marginBottom: 12, alignItems: "center" }}>
                    <button
                      className="btn ghost sm"
                      disabled={totalAvailGroups === 0}
                      onClick={selectAllGroups}
                    >
                      {I.check} Select all groups ({totalAvailGroups})
                    </button>
                    <button
                      className="btn ghost sm"
                      disabled={totalSelGroups === 0}
                      onClick={() => setGroupSel({})}
                    >
                      Clear
                    </button>
                    <span className="muted" style={{ fontSize: 12, marginLeft: "auto" }}>
                      {totalSelGroups} of {totalAvailGroups} selected
                    </span>
                  </div>
                  {[...campaignIds].map((cid) => (
                    <CampaignGroupTree
                      key={cid}
                      campaignId={cid}
                      campaignName={campaignName(cid)}
                      selected={groupSel[cid] || []}
                      onToggle={(agid) => toggleGroup(cid, agid)}
                      onSelectAll={(ids) => setCampaignGroups(cid, ids)}
                      onGroupsLoaded={onGroupsLoaded}
                    />
                  ))}
                </WizField>
              )}
              {totalSelGroups > 1 && (
                <div className="wiz-callout">
                  {I.sparkles}
                  <span>
                    <b>{totalSelGroups} independent group tests</b> will be created
                    {[...campaignIds].filter((c) => (groupSel[c] || []).length).length > 1
                      ? ` across ${[...campaignIds].filter((c) => (groupSel[c] || []).length).length} campaigns`
                      : ""}{" "}
                    — each group gets its own table, winner and Apply.
                  </span>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="wiz-pane">
              <div className="wiz-pane-head">
                <h3>Pages in the test</h3>
                <p>Pick the Custom Product Pages (and the default page) to compare — 2 to 10.</p>
              </div>
              <WizField label={`Pages from App Store Connect — ${variantCount}/10 in test`}>
                {cpps.isLoading ? (
                  <span className="muted" style={{ fontSize: 13 }}>Loading pages…</span>
                ) : (
                  <div className="wiz-libgrid">
                    {cppRows.map((p) => {
                      const k = cppKey(p);
                      const on = pickedKeys.includes(k);
                      return (
                        <button
                          key={k}
                          className={"wiz-lib " + (on ? "active" : "")}
                          onClick={() => toggleKey(k)}
                          disabled={!on && variantCount >= 10}
                        >
                          <CppMiniShots shots={p.screenshots} />
                          <span className="wiz-lib-meta">
                            <b>{p.name}</b>
                            <span className="muted">{p.screenshots.length} shots</span>
                          </span>
                          <span className={"mx-check " + (on ? "on" : "")}>{on ? I.check : I.plus}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </WizField>
              {variantCount < 2 && (
                <div className="wiz-hint">Pick at least two pages to compare.</div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="wiz-pane">
              <div className="wiz-pane-head">
                <h3>Impression split</h3>
                <p>How much traffic each variant gets. Must add up to 100%. Skewed splits are allowed.</p>
              </div>
              <div className="wiz-split">
                {picked.map((p, i) => (
                  <div className="wiz-split-row" key={cppKey(p)}>
                    <CppMiniShots shots={p.screenshots} />
                    <span className="wiz-split-name">{p.name}</span>
                    <div className="wiz-split-track">
                      <span style={{ width: splitVals[i] + "%" }} />
                    </div>
                    <div className="input-group" style={{ width: 96 }}>
                      <input
                        className="input num"
                        value={splitVals[i]}
                        onChange={(e) => setSplitAt(i, e.target.value)}
                        style={{ textAlign: "right" }}
                      />
                      <span className="muted">%</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="wiz-split-foot">
                <button className="btn sm" onClick={() => setSplit(null)}>
                  Reset to even
                </button>
                <span className={"wiz-sum " + (splitSum === 100 ? "ok" : "bad")}>
                  Total: {splitSum}%{splitSum !== 100 && " — must equal 100%"}
                </span>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="wiz-pane">
              <div className="wiz-pane-head">
                <h3>Winner metric</h3>
                <p>Pick the single metric that decides the winner.</p>
              </div>
              <div className="wiz-metrics">
                {METRIC_OPTIONS.map((m) => (
                  <button
                    key={m.id}
                    className={"wiz-metric " + (metric === m.id ? "active" : "")}
                    onClick={() => setMetric(m.id)}
                  >
                    <span className={"bk-radio " + (metric === m.id ? "checked" : "")}>
                      <span className="bk-radio-dot" />
                    </span>
                    <span className="wiz-metric-meta">
                      <b>{m.label}</b>
                      <span className="muted">{m.hint}</span>
                    </span>
                    {CPP_METRIC_META[m.id].impressionBased ? (
                      <Badge kind="accent">ETA shown</Badge>
                    ) : (
                      <Badge kind="paused">Matures</Badge>
                    )}
                  </button>
                ))}
              </div>
              {metric === "cpa" && (
                <div className="wiz-grid-2" style={{ marginTop: 4 }}>
                  <WizField label="In-app event">
                    <select className="select" value={event} onChange={(e) => setEvent(e.target.value)}>
                      <option value="">Select event…</option>
                      {(events.data || []).map((ev) => (
                        <option key={ev.value} value={ev.value}>
                          {ev.label}
                        </option>
                      ))}
                    </select>
                  </WizField>
                  <WizField label="Day after install">
                    <select className="select" value={day} onChange={(e) => setDay(e.target.value)}>
                      {DAY_OPTIONS.map((d) => (
                        <option key={d} value={d}>
                          D{d}
                        </option>
                      ))}
                    </select>
                  </WizField>
                </div>
              )}
              {metric === "roas" && (
                <WizField label="Day after install" hint="Pulled from connected analytics.">
                  <select
                    className="select"
                    value={day}
                    onChange={(e) => setDay(e.target.value)}
                    style={{ maxWidth: 240 }}
                  >
                    {DAY_OPTIONS.map((d) => (
                      <option key={d} value={d}>
                        D{d}
                      </option>
                    ))}
                  </select>
                </WizField>
              )}
              {(metric === "roas" || metric === "cpa") && (
                <div className="wiz-callout">
                  {I.sparkles}
                  <span>
                    These figures come from your connected analytics provider and mature over time,
                    so no end date is shown.
                  </span>
                </div>
              )}
            </div>
          )}

          {step === 5 && (
            <div className="wiz-pane">
              <div className="wiz-pane-head">
                <h3>Confidence &amp; improvement</h3>
                <p>Both fields are required. The defaults suit most tests.</p>
              </div>
              <div className="wiz-grid-2">
                <WizField
                  label="Confidence level"
                  hint="How sure we want to be before calling a winner. Higher = stricter, slower."
                >
                  <select
                    className="select"
                    value={confidence}
                    onChange={(e) => setConfidence(Number(e.target.value) as 90 | 95 | 99)}
                  >
                    <option value={90}>90%</option>
                    <option value={95}>95% (recommended)</option>
                    <option value={99}>99%</option>
                  </select>
                </WizField>
                <WizField
                  label="Minimum improvement"
                  hint="How much better a page must be to count as a real win, not noise."
                >
                  <select
                    className="select"
                    value={threshold}
                    onChange={(e) => setThreshold(Number(e.target.value))}
                  >
                    {[1, 2, 3, 5, 10].map((t) => (
                      <option key={t} value={t}>
                        {t}%{t === 3 ? " (recommended)" : ""}
                      </option>
                    ))}
                  </select>
                </WizField>
              </div>
              {metricMeta.impressionBased ? (
                <div className="wiz-estimate">
                  <span className="wiz-est-ico">{I.clock}</span>
                  <div className="wiz-est-meta">
                    <span className="wiz-est-k">Estimated duration</span>
                    <span className="wiz-est-v">
                      {estimateMut.isPending
                        ? "calculating…"
                        : estimateMut.data?.estimated_days != null
                          ? `≈ ${estimateMut.data.estimated_days} days`
                          : "—"}
                    </span>
                  </div>
                  <span className="wiz-est-hint">
                    {estimateMut.data?.note ||
                      "Based on current traffic, the split and your settings. Tightens as real data comes in."}
                  </span>
                </div>
              ) : (
                <div className="wiz-estimate muted-card">
                  <span className="wiz-est-ico">{I.sparkles}</span>
                  <div className="wiz-est-meta">
                    <span className="wiz-est-k">No fixed end date</span>
                    <span className="wiz-est-v">Matures with data</span>
                  </div>
                  <span className="wiz-est-hint">
                    {metricMeta.short} resolves on post-install maturity, so no estimate is shown.
                  </span>
                </div>
              )}
            </div>
          )}

          {step === 6 && (
            <div className="wiz-pane">
              <div className="wiz-pane-head">
                <h3>When the test ends</h3>
                <p>Roll-out behaviour and optional stop conditions.</p>
              </div>
              <button
                className={"wiz-toggle-card " + (autoReplace ? "active" : "")}
                onClick={() => setAutoReplace(!autoReplace)}
              >
                <span className="wiz-tc-meta">
                  <b>Auto-replace the winner</b>
                  <span className="muted">
                    Roll out the confirmed winner automatically. Off = wait for a manual Apply.
                  </span>
                </span>
                <span className={"switch " + (autoReplace ? "on" : "")} />
              </button>

              <div className="wiz-section-lbl">
                Stop conditions <span className="wiz-section-opt">· optional — pick none, one or both</span>
              </div>
              <div className="wiz-stop">
                <div className={"wiz-stop-row " + (durOn ? "active" : "")}>
                  <button className="wiz-stop-toggle" onClick={() => setDurOn(!durOn)}>
                    <span className={"switch " + (durOn ? "on" : "")} />
                    <span className="wiz-stop-meta">
                      <b>Maximum test duration</b>
                      <span className="muted">Stop after a fixed number of days.</span>
                    </span>
                  </button>
                  {durOn && (
                    <select
                      className="select"
                      value={maxDur}
                      onChange={(e) => setMaxDur(Number(e.target.value))}
                      style={{ width: 140 }}
                    >
                      {[14, 21, 30, 45, 60].map((d) => (
                        <option key={d} value={d}>
                          {d} days
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className={"wiz-stop-row " + (capOn ? "active" : "")}>
                  <button className="wiz-stop-toggle" onClick={() => setCapOn(!capOn)}>
                    <span className={"switch " + (capOn ? "on" : "")} />
                    <span className="wiz-stop-meta">
                      <b>Spend cap</b>
                      <span className="muted">Stop once the test has spent this much.</span>
                    </span>
                  </button>
                  {capOn && (
                    <div className="input-group" style={{ width: 140 }}>
                      <span className="muted">$</span>
                      <input
                        className="input num"
                        value={spendCap}
                        onChange={(e) => setSpendCap(Number(e.target.value || "0"))}
                      />
                    </div>
                  )}
                </div>
              </div>

              {(durOn || capOn) && (
                <WizField label="If a stop condition is hit with no significant difference">
                  <div className="wiz-radio-row">
                    {[
                      { v: "keep" as const, l: "Keep the current page" },
                      { v: "manual" as const, l: "Leave it to me" },
                    ].map((o) => (
                      <button
                        key={o.v}
                        className={"wiz-rcard " + (noWin === o.v ? "active" : "")}
                        onClick={() => setNoWin(o.v)}
                      >
                        <span className={"bk-radio " + (noWin === o.v ? "checked" : "")}>
                          <span className="bk-radio-dot" />
                        </span>
                        {o.l}
                      </button>
                    ))}
                  </div>
                </WizField>
              )}
            </div>
          )}

          {step === 7 && (
            <div className="wiz-pane">
              <div className="wiz-pane-head">
                <h3>Name &amp; launch</h3>
                <p>Give the test a name for the history, then review.</p>
              </div>
              <WizField label="Test name">
                <input
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Hero vs Feature-first — Q2"
                />
              </WizField>
              <div className="wiz-summary">
                {(
                  [
                    ["App", app?.name || "—"],
                    [
                      "Campaigns",
                      [...campaignIds]
                        .filter((c) => (groupSel[c] || []).length > 0)
                        .map((c) => campaignName(c))
                        .join(", ") || "—",
                    ],
                    [
                      "Ad-groups",
                      `${totalSelGroups}${totalSelGroups > 1 ? " (independent tests)" : ""}`,
                    ],
                    ["Pages", `${variantCount} per group`],
                    ["Split", splitVals.join(" / ") + "%"],
                    [
                      "Metric",
                      metricMeta.label +
                        (metric === "cpa"
                          ? ` · ${events.data?.find((e) => e.value === event)?.label || event} · D${day}`
                          : metric === "roas"
                            ? ` · D${day}`
                            : ""),
                    ],
                    ["Confidence", confidence + "%"],
                    ["Min improvement", threshold + "%"],
                    ["Auto-replace", autoReplace ? `On · ≥${threshold}% / ${confidence}%` : "Off — manual Apply"],
                    [
                      "Stop conditions",
                      [durOn && `${maxDur} days`, capOn && `$${spendCap.toLocaleString()} cap`]
                        .filter(Boolean)
                        .join(" · ") || "None — run until manual stop",
                    ],
                  ] as [string, string][]
                ).map(([k, v]) => (
                  <div className="wiz-sum-row" key={k}>
                    <span className="wiz-sum-k">{k}</span>
                    <span className="wiz-sum-v">{v}</span>
                  </div>
                ))}
              </div>
              {createMut.isError && (
                <div className="wiz-callout" style={{ borderColor: "var(--c-neg-500)" }}>
                  {I.sparkles}
                  {/* Say what the server said. This used to name one cause for
                      every failure, so a 500 read as "your ad groups are busy"
                      and sent the user looking for a conflict that wasn't
                      there. The busy case still has its own hint below, but
                      only as a hint — never as a diagnosis. */}
                  <span>{createErrorText(createMut.error)}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="cpp-wiz-foot">
          <button className="btn ghost" onClick={back}>
            {step === 1 ? "Cancel" : "Back"}
          </button>
          <span className="muted" style={{ fontSize: 12 }}>
            Step {step} of 7
          </span>
          <button className="btn primary" disabled={!canNext || createMut.isPending} onClick={next}>
            {step === 7 ? <>{I.play} Launch test</> : <>Continue {I.chevron}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
